import { useCallback, useState } from "react";

interface UseWidgetTableViewResult {
  isTableView: (widgetId: string) => boolean;
  toggleTableView: (widgetId: string) => void;
  /** Drops a widget back to the chart view — used after an update or a removal. */
  resetTableView: (widgetId: string) => void;
}

/**
 * Tracks which widget cards are showing their table view, keyed by widget id.
 *
 * The state lives above the cards because expanding/collapsing the summary
 * panel swaps between a grid and a list layout, which remounts every
 * `DatasetWidgetCard`. Card-local state would be lost on every maximize.
 */
export default function useWidgetTableView(): UseWidgetTableViewResult {
  const [tableViewIds, setTableViewIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const isTableView = useCallback(
    (widgetId: string) => tableViewIds.has(widgetId),
    [tableViewIds]
  );

  const toggleTableView = useCallback((widgetId: string) => {
    setTableViewIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(widgetId)) next.add(widgetId);
      return next;
    });
  }, []);

  const resetTableView = useCallback((widgetId: string) => {
    setTableViewIds((prev) => {
      if (!prev.has(widgetId)) return prev;
      const next = new Set(prev);
      next.delete(widgetId);
      return next;
    });
  }, []);

  return { isTableView, toggleTableView, resetTableView };
}
